<template>
  <!-- eslint-disable vue/valid-v-slot -->
  <div>
    <v-data-table
      :headers="headers"
      :items="periods"
      :loading="loading"
      class="elevation-1"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-options': [5, 10, 15, 25],
        'items-per-page-text': $t('general.itemsPerPage'),
      }"
      :no-data-text="$t('colleges.no_periods_found')"
    >
      <template #item.name="{ item }">
        <span class="font-weight-medium">{{ item.name }}</span>
      </template>

      <template #item.startDate="{ item }">
        {{ formatDate(item.startDate) }}
      </template>

      <template #item.endDate="{ item }">
        {{ formatDate(item.endDate) }}
      </template>

      <template #item.type="{ item }">
        <v-chip
          :color="item.calendarTypeId ? 'primary' : 'secondary'"
          text-color="white"
          small
        >
          <v-icon left small>
            {{ item.calendarTypeId ? 'mdi-calendar-check' : 'mdi-calendar-edit' }}
          </v-icon>
          {{ item.calendarTypeId ? $t('colleges.default_period') : $t('colleges.custom_period') }}
        </v-chip>
      </template>

      <template #item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="$emit('edit-period', item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>
            {{ editTooltip(item) }}
          </span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="error"
              :disabled="!!item.calendarTypeId || hasStarted(item)"
              v-bind="attrs"
              v-on="on"
              @click="$emit('delete-period', item)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>
            {{ deleteTooltip(item) }}
          </span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import DateUtils from '@/utils/date.utils';
import { createLocalDate, hasPeriodStarted, hasPeriodEnded } from '@/utils/academicPeriodValidations';

export default {
  name: 'AcademicPeriodsDataTable',
  props: {
    periods: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('colleges.period_name'),
          value: 'name',
          sortable: true,
        },
        {
          text: this.$t('colleges.start_date'),
          value: 'startDate',
          sortable: true,
        },
        {
          text: this.$t('colleges.end_date'),
          value: 'endDate',
          sortable: true,
        },
        {
          text: this.$t('colleges.period_type'),
          value: 'type',
          sortable: true,
          align: 'center',
        },
        {
          text: this.$t('general.actions'),
          value: 'actions',
          sortable: false,
          align: 'center',
        },
      ];
    },
  },
  methods: {
    hasStarted(period) {
      return hasPeriodStarted(period.startDate);
    },
    hasEnded(period) {
      return hasPeriodEnded(period.endDate);
    },
    editTooltip(period) {
      if (this.hasEnded(period)) {
        return this.$t('colleges.edit_ended_period');
      }

      if (this.hasStarted(period)) {
        return this.$t('colleges.edit_started_period');
      }

      return this.$t('colleges.edit_period');
    },
    deleteTooltip(period) {
      if (period.calendarTypeId) {
        return this.$t('colleges.cannot_delete_default');
      }

      if (this.hasStarted(period)) {
        return this.$t('colleges.cannot_delete_started');
      }

      return this.$t('colleges.delete_period');
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      // Normalize date to YYYY-MM-DD to avoid timezone shifts
      const normalizedDate = DateUtils.normalizeDateToYYYYMMDD(dateString);

      if (!normalizedDate) return '-';

      const date = createLocalDate(normalizedDate);
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.v-chip {
  margin: 2px;
}
</style>
